import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import SEOHead from '../components/shared/SEOHead';
import TuscanyMap from '../components/shared/TuscanyMap';
import { FadeInOnScroll } from '../components/shared/Parallax';
import { pageSEO, locationSEO, generateBreadcrumbSchema } from '../utils/seo';
import { MapPin, ArrowRight, Check } from 'lucide-react';

const AreeServitePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const locations = [
    {
      id: 'pisa',
      name: 'Pisa',
      description: 'Sede principale e area di massima copertura. Installazioni in tutta la città e zone costiere.',
      link: '/aree-servite/pisa',
      primary: true,
      areas: locationSEO.pisa.content.areas
    },
    {
      id: 'provincia-pisa',
      name: 'Provincia di Pisa',
      description: 'Copertura completa della provincia: Cascina, Pontedera, San Giuliano Terme, Volterra e tutti i comuni.',
      link: '/aree-servite/provincia-pisa',
      primary: true,
      areas: locationSEO.provinciaPisa.content.towns.slice(0, 5)
    },
    {
      id: 'lucca',
      name: 'Lucca',
      description: 'Dalla città murata alla Versilia, dalla Piana di Lucca alla Garfagnana.',
      link: '/aree-servite/lucca',
      areas: locationSEO.lucca.content.areas.slice(0, 5)
    },
    {
      id: 'livorno',
      name: 'Livorno',
      description: 'Costa degli Etruschi, dal porto di Livorno fino a Piombino. Strutture resistenti al mare.',
      link: '/aree-servite/livorno',
      areas: locationSEO.livorno.content.areas.slice(0, 5)
    },
    {
      id: 'firenze',
      name: 'Firenze',
      description: 'Soluzioni di lusso per la città del Rinascimento e le colline del Chianti.',
      link: '/aree-servite/firenze',
      areas: locationSEO.firenze.content.areas.slice(0, 5)
    },
    {
      id: 'massa-carrara',
      name: 'Massa-Carrara',
      description: 'Dalla costa della Versilia settentrionale alla Lunigiana.',
      link: '/aree-servite/massa-carrara',
      areas: locationSEO.massaCarrara.content.areas.slice(0, 5)
    },
    {
      id: 'pistoia',
      name: 'Pistoia',
      description: 'Città del verde e Valdinievole, da Montecatini a Pescia.',
      link: '/aree-servite/pistoia',
      areas: locationSEO.pistoia.content.areas.slice(0, 5)
    }
  ];

  const services = [
    "Pergole bioclimatiche con lamelle orientabili",
    "Pergotende su misura",
    "Tende da sole motorizzate",
    "Vetrate panoramiche scorrevoli",
    "Tende da interno artigianali",
    "Consulenza e progettazione",
    "Installazione professionale",
    "Assistenza post-vendita"
  ];

  return (
    <Layout>
      <SEOHead
        title={pageSEO.areeServite.title}
        description={pageSEO.areeServite.description}
        keywords={pageSEO.areeServite.keywords}
        schema={generateBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Aree Servite', path: '/aree-servite' }
        ])}
      />

      {/* Hero Section */}
      <HeroSection
        title="Installiamo in Tutta la Toscana"
        subtitle="Da Pisa a Firenze, da Lucca a Livorno: portiamo l'eccellenza dell'outdoor living in ogni angolo della regione"
        ctaText="Richiedi sopralluogo gratuito"
        ctaLink="/preventivo"
        imageUrl="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80"
        showScrollIndicator={true}
        minHeight="70vh"
      />

      {/* Intro Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Aree di Intervento
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                {pageSEO.areeServite.h1}
              </h1>
              <p className="text-lg text-gray-600 font-light leading-relaxed">
                Con sede a Pisa, serviamo tutta la Toscana con installazioni di <strong>pergole bioclimatiche</strong>, 
                <strong> pergotende</strong>, <strong>tende da sole</strong> e <strong>vetrate panoramiche</strong>. 
                Oltre 60 anni di esperienza ci permettono di offrire soluzioni su misura per il clima 
                mediterraneo toscano, valorizzando ville, terrazzi e spazi commerciali.
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-12">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Mappa Interattiva
              </span>
              <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-4">
                Clicca sulla Provincia per Scoprire i Dettagli
              </h2>
              <p className="text-gray-600 font-light max-w-xl mx-auto">
                Esplora le aree in cui operiamo. Ogni provincia ha una pagina dedicata con informazioni 
                sui servizi disponibili nella zona.
              </p>
            </div>
          </FadeInOnScroll>
          <FadeInOnScroll delay={200}>
            <TuscanyMap className="max-w-3xl mx-auto" />
          </FadeInOnScroll>
        </div>
      </section>

      {/* Locations Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Zone Servite"
              title="Dove Operiamo"
              description="Copertura capillare in tutta la Toscana, con particolare focus su Pisa e provincia."
            />
          </FadeInOnScroll>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location, index) => (
              <FadeInOnScroll key={location.id} delay={index * 100}>
                <Link 
                  to={location.link}
                  className={`block p-8 bg-white border transition-all duration-500 hover:shadow-xl group ${
                    location.primary 
                      ? 'border-sky-200 hover:border-sky-400' 
                      : 'border-gray-100 hover:border-sky-200'
                  }`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      location.primary ? 'bg-sky-500 text-white' : 'bg-sky-500/10 text-sky-500'
                    }`}>
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-gray-900 group-hover:text-sky-500 transition-colors">
                        {location.name}
                      </h3>
                      {location.primary && (
                        <span className="text-xs text-sky-500 font-medium uppercase tracking-wider">
                          Area Principale
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-600 font-light mb-4">
                    {location.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {location.areas.map((area, aIndex) => (
                      <span 
                        key={aIndex}
                        className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center text-sky-500 text-sm font-medium group-hover:gap-3 gap-2 transition-all">
                    Scopri di più
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Services Available Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  I Nostri Servizi
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                  Cosa Installiamo in Toscana
                </h2>
                <p className="text-gray-600 font-light leading-relaxed mb-8">
                  In ogni area della Toscana offriamo la gamma completa dei nostri servizi, 
                  dalla consulenza iniziale all'installazione, fino all'assistenza post-vendita.
                </p>
                <ul className="space-y-3">
                  {services.map((service, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-sky-500" />
                      </div>
                      <span className="text-gray-700 font-light">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"
                  alt="Installazione pergola bioclimatica Toscana"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-sky-500 text-white p-6 hidden lg:block">
                  <div className="text-4xl font-light mb-1">7</div>
                  <div className="text-sm uppercase tracking-wider">Province servite</div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Why Local Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Perché Sceglierci
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                Vantaggi di un Partner Locale
              </h2>
            </div>
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {[
              {
                title: "Conoscenza del Territorio",
                description: "Conosciamo il clima toscano, le normative locali e le caratteristiche architettoniche della regione."
              },
              {
                title: "Interventi Rapidi",
                description: "Essendo basati a Pisa, garantiamo tempi di risposta brevi per sopralluoghi e assistenza in tutta la Toscana."
              },
              {
                title: "Referenze Locali",
                description: "Centinaia di installazioni realizzate nella regione, verificabili e visitabili su richiesta."
              }
            ].map((item, index) => (
              <FadeInOnScroll key={index} delay={index * 150}>
                <div className="text-center p-8">
                  <h3 className="text-xl font-medium text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 font-light">{item.description}</p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Sei in Toscana? Contattaci"
        description="Sopralluogo gratuito e preventivo personalizzato per la tua zona."
        variant="dark"
      />
    </Layout>
  );
};

export default AreeServitePage;
